import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faEye, faEdit, faTrash, faCopy, 
    faCalendarAlt, faClock, faMapMarkerAlt, faVideo,
    faBell, faShare, faDownload, faPrint
} from '@fortawesome/free-solid-svg-icons';

interface ContextMenuProps {
    show: boolean;
    x: number;
    y: number;
    date?: Date;
    view?: 'month' | 'week' | 'day' | 'agenda';
    onClose: () => void;
    onCreateEvent: (date?: Date) => void;
    onCreateTimer: () => void;
    onViewDate: (date: Date) => void;
    onCopyDate: (date: Date) => void;
    onExportCalendar: () => void;
    onPrintCalendar: () => void;
    onShareCalendar: () => void;
    onSetReminder: (date: Date) => void;
}

const CalendarContextMenu: React.FC<ContextMenuProps> = ({
    show,
    x,
    y,
    date,
    view = 'month',
    onClose,
    onCreateEvent,
    onCreateTimer,
    onViewDate,
    onCopyDate,
    onExportCalendar,
    onPrintCalendar,
    onShareCalendar,
    onSetReminder
}) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (show) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [show, onClose]);

    if (!show) return null;

    const handleAction = (action: () => void) => {
        action();
        onClose();
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div
            ref={menuRef}
            className="calendar-context-menu"
            style={{
                position: 'fixed',
                left: x,
                top: y,
                zIndex: 9999
            }}
        >
            <div className="context-menu-header">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                {date ? formatDate(date) : 'Calendar Actions'}
            </div>
            
            <div className="context-menu-divider"></div>
            
            {/* Primary Actions */}
            <div className="context-menu-section">
                <div 
                    className="context-menu-item"
                    onClick={() => handleAction(() => onCreateEvent(date))}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Create Event
                </div>
                
                <div 
                    className="context-menu-item"
                    onClick={() => handleAction(() => onCreateTimer())}
                >
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Create Timer
                </div>
                
                {date && (
                    <div 
                        className="context-menu-item"
                        onClick={() => handleAction(() => onViewDate(date))}
                    >
                        <FontAwesomeIcon icon={faEye} className="me-2" />
                        View Day Details
                    </div>
                )}
            </div>
            
            <div className="context-menu-divider"></div>
            
            {/* Date Actions */}
            {date && (
                <div className="context-menu-section">
                    <div 
                        className="context-menu-item"
                        onClick={() => handleAction(() => onCopyDate(date))}
                    >
                        <FontAwesomeIcon icon={faCopy} className="me-2" />
                        Copy Date
                    </div>
                    
                    <div 
                        className="context-menu-item"
                        onClick={() => handleAction(() => onSetReminder(date))}
                    >
                        <FontAwesomeIcon icon={faBell} className="me-2" />
                        Set Reminder
                    </div>
                </div>
            )}
            
            <div className="context-menu-divider"></div>
            
            {/* Calendar Actions */}
            <div className="context-menu-section">
                <div 
                    className="context-menu-item"
                    onClick={() => handleAction(() => onExportCalendar())}
                >
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Export Calendar
                </div>
                
                <div 
                    className="context-menu-item"
                    onClick={() => handleAction(() => onPrintCalendar())}
                >
                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                    Print Calendar
                </div>
                
                <div 
                    className="context-menu-item"
                    onClick={() => handleAction(() => onShareCalendar())}
                >
                    <FontAwesomeIcon icon={faShare} className="me-2" />
                    Share Calendar
                </div>
            </div>
        </div>
    );
};

export default CalendarContextMenu;
